import { createContext, useContext, useLayoutEffect, useRef, useState, type ReactNode } from 'react'
import { createPortal } from 'react-dom'

const ContentAreaDialogContext = createContext<{
  target: HTMLDivElement | null
  setActive: (active: boolean) => void
} | null>(null)

/** Only the route content is covered/inert; the shell's navigation stays usable. */
export function ContentAreaDialogRegion({ children, onActiveChange }: {
  children: ReactNode
  onActiveChange: (active: boolean) => void
}) {
  const [target, setTarget] = useState<HTMLDivElement | null>(null)
  const [active, setActive] = useState(false)
  const background = useRef<HTMLDivElement>(null)

  useLayoutEffect(() => {
    background.current?.toggleAttribute('inert', active)
    onActiveChange(active)
  }, [active, onActiveChange])

  return <ContentAreaDialogContext.Provider value={{ target, setActive }}>
    <div className="relative flex min-h-0 min-w-0 flex-1 flex-col">
      <div ref={background} aria-hidden={active || undefined} className={`flex min-h-0 min-w-0 flex-1 flex-col ${active ? 'invisible' : ''}`}>
        {children}
      </div>
      <div ref={setTarget} data-content-dialog-host="" className={active ? 'absolute inset-0 z-30' : 'hidden'} />
    </div>
  </ContentAreaDialogContext.Provider>
}

export default function ContentAreaDialog({ children, labelledBy, describedBy }: {
  children: ReactNode
  labelledBy: string
  describedBy: string
}) {
  const region = useContext(ContentAreaDialogContext)
  const dialog = useRef<HTMLDivElement>(null)
  const setActive = region?.setActive
  const target = region?.target

  useLayoutEffect(() => {
    setActive?.(true)
    return () => setActive?.(false)
  }, [setActive])

  useLayoutEffect(() => {
    const previous = document.activeElement
    const element = dialog.current
    element?.focus({ preventScroll: true })
    return () => {
      // Do not steal focus back from a shell menu the user deliberately opened.
      if (element?.contains(document.activeElement) && previous instanceof HTMLElement && previous.isConnected) previous.focus({ preventScroll: true })
    }
  }, [target])

  const content = <div ref={dialog} role="dialog" aria-modal="false" aria-labelledby={labelledBy} aria-describedby={describedBy} tabIndex={-1}
    className="h-full overflow-y-auto overscroll-contain bg-gray-50 p-4 text-gray-900 focus:outline-none dark:bg-[#0c0d14] dark:text-white sm:p-6 lg:p-8">
    {children}
  </div>
  return target ? createPortal(content, target) : region ? null : content
}
